<script setup lang="ts">
import { Dialog } from "../../../../base-components/Headless";
import { FormLabel, FormInput } from "../../../../base-components/Form";
import Button from "../../../../base-components/Button";
import Table from "../../../../base-components/Table";
import LoadingIcon from "../../../../base-components/LoadingIcon";

import { computed } from "vue";
import { useCollection } from "vuefire";
import { collection, getFirestore, orderBy, query } from "firebase/firestore";

import { userUserStore } from "../../../../stores/user";
import { formatearFechaTimestampDDMMYYYY } from "../../../../utils/fecha";
import { numeroFormateado } from "../../../../utils/helper";
import {
  Pedido,
  pedidoItemConverter,
  PedidoItem
} from "@fixeduy/restaurantestypes";
import { utilFechaConvertirNumeroString } from "../../../../utils/fechaFixedTypes";

interface Props {
  pedido: Pedido;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "cerrar"): void;
}>();

const sendButtonRef = null;

const userStore = userUserStore();
const db = getFirestore();

const identificador = computed(() => {
  if (!props.pedido) return "";
  return `${props.pedido.mesaNombre} | ${utilFechaConvertirNumeroString(
    props.pedido.fechaCreacion,
    true,
    "/"
  )}`;
});

const itemsQuery = computed(() => {
  const empresaId = userStore.empresa?.id;
  const pedidoId = props.pedido?.id;

  if (!empresaId || !pedidoId) return null;

  return query(
    collection(
      db,
      `empresas/${empresaId}/pedidos/${pedidoId}/pedidos-items`
    ).withConverter(pedidoItemConverter),
    orderBy("fechaCreacion", "desc")
  );
});

const pedidosLineas = useCollection<PedidoItem>(itemsQuery);
</script>

<template>
  <Dialog
    :open="true"
    @close="() => emit('cerrar')"
    :initialFocus="sendButtonRef as any"
    size="xxl"
  >
    <Dialog.Panel class="overflow-hidden flex flex-col max-h-[85vh]">
      <Dialog.Title>
        <h2 class="mr-auto text-base font-medium">
          Información ampliada pedido # {{ props.pedido.numero }}
        </h2>
      </Dialog.Title>

      <Dialog.Description>
        <div class="flex">
          <div class="flex-none w-50 pr-5 mt-2">
            <FormLabel htmlFor="horizontal-form-1 ml-1">Nombre</FormLabel>
          </div>
          <div class="flex-1 ml-2">
            <FormInput type="text" disabled :value="identificador" />
          </div>
        </div>
      </Dialog.Description>

      <div class="m-6 overflow-x-auto">
        <Table bordered striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th class="whitespace-nowrap">Estado</Table.Th>
              <Table.Th class="whitespace-nowrap">Cantidad</Table.Th>
              <Table.Th class="whitespace-nowrap">Hora</Table.Th>
              <Table.Th class="whitespace-nowrap"
                >Detalle (cant. | descripción | importe)</Table.Th
              >
              <Table.Th class="whitespace-nowrap text-right"
                >P. Unitario</Table.Th
              >
              <Table.Th class="whitespace-nowrap text-right">Total</Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            <!-- Rows -->
            <Table.Tr v-for="linea in pedidosLineas" :key="linea.id">
              <Table.Td>{{ linea.estado }}</Table.Td>
              <Table.Td>{{ linea.cantidad }}</Table.Td>
              <Table.Td>
                <!-- Si tu fechaCreacion es Timestamp, cambiá según tu helper -->
                {{
                  new Date(
                    (linea.fechaCreacion as any)?.toMillis?.() ??
                      linea.fechaCreacion
                  ).toLocaleTimeString("es-UY", {
                    hour: "2-digit",
                    minute: "2-digit"
                  })
                }}
              </Table.Td>
              <Table.Td>{{ linea.nombre }}</Table.Td>
              <Table.Td class="text-right">
                {{ numeroFormateado(linea.precioUnitarioConIva) }}
              </Table.Td>
              <Table.Td class="text-right">
                {{ numeroFormateado(linea.total) }}
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </div>

      <Dialog.Footer>
        <Button variant="secondary" class="mb-2 mr-3" @click="emit('cerrar')">
          Cerrar
        </Button>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>
